import { useLazyQuery } from "@apollo/client";
import { GOOGLE_OAUTH_QUERY } from "apollo";
import {
  Button,
  ButtonWithGradient,
  GoogleOauthBtn,
  MultiplayerServicesLayout,
  PageLayout,
  Separator,
  Text,
} from "components";
import { UserContext } from "context";
import { useDemoMode } from "hooks";
import React, { useContext, useState } from "react";
import { useNavigate } from "react-router-dom";
import { GoogleOauthQueryDto, ServiceName } from "types";
import { E_Route, MultiplayerServices } from "utils/constants";
import { InfoItem, SecurityDialog } from "../components";
import { GoogleOauthSectionStyle, OnboardingBtnSectionStyle } from "../styles";
import { OnboardingContainer } from "./styles";

const defaultScopes = [
  "https://www.googleapis.com/auth/userinfo.email",
  "https://www.googleapis.com/auth/userinfo.profile",
];

export const OnboardingScreen: React.FC = () => {
  const [selectedServices, setSelectedServices] = useState<ServiceName[]>(
    MultiplayerServices.map((s) => s.id),
  );
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);

  const { activateDemoMode, deactivateDemoMode } = useDemoMode();

  const { state: userState } = useContext(UserContext);

  const navigate = useNavigate();

  const [getOauthUrl] = useLazyQuery<any, GoogleOauthQueryDto>(
    GOOGLE_OAUTH_QUERY,
  );

  const startOauth = () => {
    deactivateDemoMode(true);
    const requiredScopes = selectedServices.reduce((acc, service) => {
      const serviceData = MultiplayerServices.find((s) => s.id === service);
      if (serviceData) {
        const newScopes = serviceData.scopes.filter(
          (scope) => !acc.includes(scope),
        );
        return [...acc, ...newScopes];
      }
      return acc;
    }, [] as string[]);

    getOauthUrl({
      variables: {
        oauthData: {
          redirect: `${process.env.REACT_APP_HOST_URL}${E_Route.Onboarding}`,
          type: "gmail",
          scopes: [...defaultScopes, ...requiredScopes],
          services: selectedServices,
        },
        userId: userState?._id,
      },
    })
      .then((res) => {
        if (res?.data?.googleOAuthUrl) {
          const url = res.data?.googleOAuthUrl;
          window.location.href = url;
        }
      })
      .catch(() => {});
  };
  const handleServiceSelected = (service: ServiceName, selected: boolean) => {
    if (selected) {
      setSelectedServices((prev) => [...prev, service]);
    } else {
      setSelectedServices((prev) => prev?.filter((s) => s !== service));
    }
  };

  const startDemoMode = () => {
    activateDemoMode();
    navigate(E_Route.Home);
  };
  const partialAccount =
    userState?.partialAccount ||
    (!userState?.hasGmail && !userState?.hasCalendar) ||
    userState?.invalidGrant;

  return (
    <PageLayout title="Onboarding | Multiplayer">
      <OnboardingContainer>
        {partialAccount && (
          <>
            <Text.H2>Let’s Connect Your Google Account</Text.H2>
            {userState?.invalidGrant && (
              <Text.Body color="seconday" style={{ textAlign: "center" }}>
                We lost connection to your Google account. Please reconnect from
                here to resume your Multiplayer experience.
              </Text.Body>
            )}
            <MultiplayerServicesLayout
              selectedServices={MultiplayerServices.map((s) => s.id)}
              onSelected={handleServiceSelected}
            />

            <Separator.Horizontal spacing={3} />
            <InfoItem
              title="Your data is private & secure"
              subtitle="Reassurance for data usage. We will never send an email on your behalf.  If you decide to part ways with Multiplayer, we will leave your inbox as it was, etc. "
              onClick={() => {
                setShowPrivacyModal(true);
              }}
            />
            <Separator.Horizontal spacing={6} />

            <div className={GoogleOauthSectionStyle}>
              <ButtonWithGradient onClick={startDemoMode}>
                Try demo mode
              </ButtonWithGradient>
              <GoogleOauthBtn onClick={startOauth} style={{ width: "200px" }} />
            </div>

            <SecurityDialog
              open={showPrivacyModal}
              onOpenChange={setShowPrivacyModal}
            />
          </>
        )}

        {!partialAccount && (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              height: "80vh",
            }}
          >
            <Text.H2>You're All Set! 🎉</Text.H2>
            <Text.H4 color="seconday" style={{ textAlign: "center" }}>
              We’ve started organizing your inbox and preparing your meetings.
              Your first daily preview is on its way!
            </Text.H4>

            <Separator.Horizontal spacing={5} />
            <div className={OnboardingBtnSectionStyle}>
              <Button
                onClick={() => {
                  navigate(E_Route.Settings);
                }}
              >
                Manage preferences
              </Button>
              <ButtonWithGradient
                onClick={() => {
                  navigate(E_Route.Home);
                }}
              >
                View my dashboard
              </ButtonWithGradient>
            </div>
          </div>
        )}
      </OnboardingContainer>
    </PageLayout>
  );
};
